import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { AppHeader } from "@/components/Layout";
import { api, errMsg } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(r.payment_status)) { setState("error"); return; }
      } catch (e) {
        // Transient errors are expected while Stripe finalises the session; keep polling but
        // surface the reason so a genuine outage is visible instead of silent.
        console.warn("[billing] payment status check failed, retrying", errMsg(e));
      }
      if (tries >= 12) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="mx-auto max-w-lg px-5 py-20 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-slate-400" />
            <h1 className="mt-6 text-2xl font-semibold text-slate-900">Confirming your payment...</h1>
            <p className="mt-2 text-slate-600">This usually takes a couple of seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h1 className="mt-6 text-3xl font-bold tracking-tight text-slate-900" data-testid="payment-success-title">
              You're protected.
            </h1>
            <p className="mt-3 text-slate-600">
              Your SMS alerts are on. We'll text you as soon as a relevant Australian food recall is published.
            </p>
            <Link to="/alerts" data-testid="payment-success-continue" className="mt-8 inline-block rounded-md bg-red-700 px-6 py-3 font-semibold text-white hover:bg-red-800">
              See recent recalls
            </Link>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-red-600" />
            <h1 className="mt-6 text-2xl font-semibold text-slate-900">We couldn't confirm that payment</h1>
            <p className="mt-3 text-slate-600">
              {state === "timeout"
                ? "It's taking longer than expected. Check your settings in a moment - if it went through, your alerts will be on."
                : "The payment did not complete. You can try again from your settings."}
            </p>
            <Link to="/settings#sms-alerts" data-testid="payment-error-settings" className="mt-8 inline-block rounded-md bg-slate-900 px-6 py-3 font-semibold text-white">
              Go to settings
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
